import { useCallback, useEffect, useRef, useState } from 'react'
import buildInfo from '../buildInfo.json'
import { isProductionEnvironment } from '../helpers/environmentHelper'

// The build this bundle was compiled from. Constant for the life of the tab, by design.
export const RUNNING_BUILD = buildInfo.build

// The placeholder both stamps carry in the repo, and the value they keep anywhere the build script
// did not run. Two sides that are equal make the whole check inert, which is the correct failure
// mode — and an invisible one.
const UNSTAMPED_BUILD = 'dev'

const POLL_INTERVAL_MS = 5 * 60 * 1000
// One auto-reload per target build per tab. Without this, a build whose two stamps disagree for
// any reason (prebuild skipped, a partially-propagated deploy) would reload the app on every
// navigation, forever. sessionStorage and not state: the flag has to survive the reload it guards.
const RELOADED_FOR_KEY = 'cubbo:reloadedForBuild'

// The kill switch, kept distinct from null on purpose.
//
// null means "no usable answer" — offline, a 5xx, HTML from a rewrite, a malformed payload — and the
// right response to that is to keep believing whatever the last good check said. `autoReload: false`
// is not the absence of an answer, it is the answer "stop", and it has to be able to UNDO a tab that
// has already gone stale: otherwise `isStale` stays latched, the navigation trigger reads that
// state, and in the one scenario the lever exists for — the reload itself misbehaving, with no way
// to ship a fix to tabs that by definition do not reload — every latched tab gets one more reload
// after the switch is thrown.
const AUTO_RELOAD_OFF = Symbol('autoReloadOff')

// Reads the deployed build, AUTO_RELOAD_OFF if the deploy has switched the feature off, or null for
// "no usable answer".
//
// version.json is SERVED rather than compiled in, so `"autoReload": false` published on the deploy
// stops every open tab within one poll interval, with no build and no release. That matters because
// the population this feature acts on is by definition the one a new deploy does not reach quickly:
// if it ever misfires on the warehouse floor, shipping a fix is not a lever anyone can pull in the
// moment.
const readDeployedBuild = async signal => {
  // no-store, or the browser answers this from the same cache that is the reason we are asking.
  const response = await fetch('/version.json', { cache: 'no-store', signal })
  if (!response.ok) return null
  const payload = await response.json()
  if (payload?.autoReload === false) return AUTO_RELOAD_OFF
  return typeof payload?.build === 'string' ? payload.build : null
}

// The one link in this mechanism that cannot be audited from the repo: Netlify's build command lives
// in its UI, not in netlify.toml, so whether `postinstall` or `prebuild` runs on a deploy is
// unverifiable here. If neither does, both stamps stay at "dev", every tab agrees with every other,
// and the feature ships completely inert — safe, but with no error and nothing to observe. One
// breadcrumb per tab so that state is visible instead of indistinguishable from working.
let inertRolloutReported = false

const reportInertRollout = () => {
  if (inertRolloutReported) return
  if (RUNNING_BUILD !== UNSTAMPED_BUILD || !isProductionEnvironment) return
  inertRolloutReported = true

  console.warn(`[stale-bundle] running build is "${UNSTAMPED_BUILD}" in production: the build stamp did not run, so the staleness check is inert`)
}

// Tests only: the flag is module-level so the warning fires once per tab, not once per mount.
export const __resetInertRolloutReport = () => {
  inertRolloutReported = false
}

// Watches for a deploy that landed after this tab loaded.
//
// Not a cache problem, despite how it looks: Netlify serves index.html and the hashed assets with
// `max-age=0, must-revalidate`, so any document load already gets the newest build. The reason
// operators sit on an old one is that nothing in a normal shift ever loads a document — the
// terminal stays on the same tab all day, React Router navigates client-side, and refreshSession
// renews the token silently so they are never bounced to the login screen. A plain reload() is
// therefore enough; there is no cache to bust and no need for the hard reload the browser will not
// let a page trigger anyway.
//
// Fails safe in every direction: a missing, unreachable or malformed version.json leaves `isStale`
// false, so a broken check can never reload anyone.
export const useBuildVersion = () => {
  const [isStale, setIsStale] = useState(false)
  const deployedBuildRef = useRef(null)
  const cancelledRef = useRef(false)
  const pendingCommitRef = useRef(null)

  // Resolves to whether this tab is behind. Callers that need an answer at a specific instant
  // (the tab going away, say) use this instead of waiting for the next poll, and pass a signal so
  // the request cannot outlive the moment it was asked about.
  const checkNow = useCallback(async signal => {
    try {
      const deployed = await readDeployedBuild(signal)
      if (cancelledRef.current) return false
      if (deployed === AUTO_RELOAD_OFF) {
        // Disarm, rather than merely decline to answer. Clearing the target disarms `reload`
        // too, so both triggers are shut off even if a route change is already in flight
        // behind this await.
        deployedBuildRef.current = null
        setIsStale(false)
        return false
      }
      if (!deployed) return false
      deployedBuildRef.current = deployed
      const stale = deployed !== RUNNING_BUILD
      setIsStale(stale)
      return stale
    } catch (_error) {
      // Offline, a 5xx, HTML from a redirect, an abort — all mean "no answer", never "stale".
      // Warehouse wifi drops constantly; none of those drops may be read as a new deploy.
      return false
    }
  }, [])

  useEffect(() => {
    cancelledRef.current = false
    reportInertRollout()

    // A hidden tab cannot be polled usefully, and doing so just burns requests on the many
    // tabs left parked on a terminal. Returning to it is handled by the caller's own listener.
    const poll = () => {
      if (document.visibilityState !== 'visible') return
      checkNow()
    }

    const intervalId = setInterval(poll, POLL_INTERVAL_MS)
    document.addEventListener('visibilitychange', poll)
    poll()

    return () => {
      cancelledRef.current = true
      clearInterval(intervalId)
      document.removeEventListener('visibilitychange', poll)
    }
  }, [checkNow])

  // Reloads unless this tab already reloaded for this same target build, which would mean the
  // reload did not resolve the mismatch and repeating it never will.
  const reloadIfNotAlreadyTried = useCallback(trigger => {
    const target = deployedBuildRef.current
    if (!target) return false
    try {
      if (window.sessionStorage.getItem(RELOADED_FOR_KEY) === target) return false
    } catch (_error) {
      // Storage blocked (private mode, a locked-down terminal profile). Losing the loop guard is
      // worse than losing the auto-reload, so do nothing.
      return false
    }

    // The flag is burned only once the document is actually going away. Writing it before the
    // reload looks equivalent and is not: a beforeunload handler elsewhere in the app can
    // cancel this unload, and a flag already written would then mark this tab as "handled" for
    // a build it never got onto — permanently stale, on every later navigation, silently.
    const commit = () => {
      try {
        window.sessionStorage.setItem(RELOADED_FOR_KEY, target)
      } catch (_error) {
        // Inert: the reload is happening either way, and the tab is about to be replaced.
      }
      window.removeEventListener('pagehide', commit)
      pendingCommitRef.current = null
    }
    if (pendingCommitRef.current) window.removeEventListener('pagehide', pendingCommitRef.current)
    pendingCommitRef.current = commit
    window.addEventListener('pagehide', commit)

    // The reload is invisible by design, which also means it is invisible to whoever has to
    // explain it later. The console carries the breadcrumb, so a session that ends mid-packing
    // can be told apart from a crash, and so "did any tab ever converge?" is answerable at all.
    // eslint-disable-next-line no-console
    console.info(`[stale-bundle] reloading ${RUNNING_BUILD} -> ${target} (${trigger})`)
    window.location.reload()
    return true
  }, [])

  return { isStale, reload: reloadIfNotAlreadyTried, checkNow }
}
